import { defaultAccountsRepository } from "api/repositories/accounts/AccountsRepository";
import { LoginResponseModel } from "api/repositories/authentications/model/LoginResponseModel";
import { OverviewServiceItem } from "api/repositories/general/model/OverviewServiceResponseModel";
import { defaultPartnersRepository } from "api/repositories/partners/PartnersRepository";
import { PartnersResponse } from "api/repositories/partners/model/PartnersResponse";
import SidebarMenuConfigRepository from "api/repositories/sidebarMenu/SidebarMenuConfigRepository";
import { SidebarMenuConfigModel } from "api/repositories/sidebarMenu/model/SidebarMenuConfigModel";
import { isEmpty, isNull, isUndefined } from "lodash";
import {
  Context,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "reflect-metadata";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { DataFormat } from "utils/format/DataFormat";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";
import { ServiceRepo } from "utils/serviceRepo/ServiceRepo";
import { IContextModel } from "./model/ContextModel";

export const LocalContext: Context<IContextModel | undefined> =
  createContext(undefined);

export const ContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<LoginResponseModel>();
  const [partnerList, setPartnerList] = useState<PartnersResponse[]>([]);
  const history = useNavigate();
  const { pathname } = useLocation();
  const [info, setInfo] = useState("");
  const token = !DataFormat.getToken()
    ? DataFormat.getAlterToken()
    : DataFormat.getToken();
  const [config, setConfig] = useState({});
  const [language, setLanguage] = useState<string>("en");
  const [serviceOptions, setServiceOptions] = useState<OverviewServiceItem[]>(
    []
  );
  const [searchParams] = useSearchParams();

  const [menuConfig, setMenuConfig] =
    useState<SidebarMenuConfigModel>(undefined);

  const checkLanguage = async () => {
    const savedLanguage = localStorage?.getItem(CacheKeyEnum.language);
    if (savedLanguage === "vi") {
      setLanguage("vi");
      strings.setLanguage("vi");
    } else {
      setLanguage("en");
      strings.setLanguage("en");
    }
  };

  const replaceToken = () => {
    if (
      !isUndefined(searchParams?.get("token")) &&
      !isNull(searchParams?.get("token")) &&
      !isEmpty(searchParams?.get("token"))
    ) {
      sessionStorage?.removeItem(CacheKeyEnum.mainToken);
      localStorage?.removeItem(CacheKeyEnum.mainToken);
      sessionStorage.setItem(
        CacheKeyEnum.alterToken,
        searchParams?.get("token")?.toString()
      );
    }
  };

  const getRoles = useCallback(async () => {
    try {
      const data = await defaultAccountsRepository?.getUser();
      if (data) {
        let userData =
          !isNull(data?.data?.target) &&
          !isUndefined(data?.data?.target) &&
          !isEmpty(data?.data?.target)
            ? data?.data?.target
            : data?.data;
        setUser(userData);

        await getOverviewServiceList();
        await getSidebar(userData?.type === 3, language, userData?.note);
      }
    } catch (error) {
      console.log(error);
      localStorage?.clear();
      sessionStorage?.clear();
      getMessage(
        error?.message ?? strings.GlobalMessage.InvalidToken,
        3,
        "error"
      );
      history("/login");
    }
  }, [history]);

  const fetchPartnerList = async () => {
    try {
      const res = await defaultPartnersRepository.getPartnerList({
        page: 0,
        limit: 500,
      });
      if (res) {
        let data = [...res?.data];
        setPartnerList(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSidebar = useCallback(
    async (role: boolean, language: string, note: string) => {
      try {
        const siderConfig = await SidebarMenuConfigRepository?.getSidebarMenu(
          role,
          language,
          note
        );
        setMenuConfig(siderConfig?.data);
        localStorage?.setItem("menu", JSON.stringify(siderConfig?.data));
      } catch (error) {
        console.log(error);
      }
    },
    [user, language]
  );

  const getOverviewServiceList = async () => {
    const cache = productCache?.getCache(CacheKeyEnum.mhubServiceList);
    if (!isEmpty(cache)) {
      setServiceOptions(cache);
    } else {
      try {
        const repo = ServiceRepo.getDashboardRepo();
        const res = await repo.getOverviewServiceList();
        if (res?.data) {
          setServiceOptions(res.data);
          productCache?.saveCache(res?.data, CacheKeyEnum.mhubServiceList);
        } else {
          setServiceOptions([]);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const getPartnerInfo = (value: any) => {
    setInfo(value);
  };

  useEffect(() => {
    replaceToken();
  }, []);

  useEffect(() => {
    if (!pathname?.includes("login")) {
      getRoles();
    }
  }, [token]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      checkLanguage();
    }
  }, [language]);

  const isAuthenticated = () => {
    return !!token;
  };

  // const getConfig = async () => {
  //   try {
  //     const res = await defaultPathConfigRepository?.configDetail(
  //       "b2c-cms-config"
  //     );
  //     if (res?.data) {
  //       setConfig(JSON.parse(res?.data?.data?.config as string));
  //     }
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  return (
    <LocalContext.Provider
      value={
        {
          user: user,
          getPartnerInfo: getPartnerInfo,
          info,
          menuConfig,
          partnerList,
          setPartnerList,
          getRoles,
          getSidebar,
          isAuthenticated,
          language,
          setLanguage,
          config,
          serviceOptions,
        } as IContextModel
      }
    >
      {children}
    </LocalContext.Provider>
  );
};

export const AuthenticationContext = () => useContext(LocalContext);
